import type { ComponentProps } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppDispatch } from "../../../store/store";
import { routes } from "../../../utils/routes";
import { showSnackbar } from "../../../redux/slices/SnackbarSlice";
import {
  clearFacebookPagesData,
  clearGBPReconnectPageData,
  clearInstagramPagesData,
} from "../../../redux/slices/MapCheckInDashboardSlice";
import {
  exchangeReconnectGBPTokenAction,
  reconnectGBPPlatformAction,
} from "../../../redux/actions/AuthActionsNew";
import {
  facebookExchangeTokenAction,
  instagramExchangeTokenAction,
  instagramPortfolioExchangeTokenAction,
  tiktokExchangeTokenAction,
  youtubeExchangeTokenAction,
} from "../../../redux/actions/MapCheckInDashboardAction";
import {
  ConnectFacebookPageAction,
  connectInstagramPortfolioAction,
} from "../../../redux/actions/AdminActions/GBPActions";
import type FacebookPageSelectionModal from "../../../components/Modals/FacebookPageSelectionModal";
import type { InstagramAccount } from "../../../components/Modals/InstagramPageSelectionModal";
import {
  ReportConnectPlatform,
  isReportConnectPlatform,
} from "../../../components/ReportEditor/connectPlatforms";

export type FacebookPage = ComponentProps<typeof FacebookPageSelectionModal>["pages"][number];

type Picker =
  | { kind: "facebook"; pages: FacebookPage[] }
  | { kind: "instagram"; accounts: InstagramAccount[]; userAccessToken?: string };

interface IGbpLocation {
  google_location_id: string;
  google_business_account_id: string;
}

interface IExchangePayload {
  data?: {
    locations?: IGbpLocation[];
    access_token?: string;
    refresh_token?: string;
    token_expiry?: string;
    pages?: FacebookPage[];
    accounts?: InstagramAccount[];
    user_access_token?: string;
  };
}

interface ICredentials {
  bridgeToken: string;
  code: string;
  workspaceId: string;
}

// YouTube and TikTok redirect back with the bridge token only
const PLATFORMS_WITH_CODE: readonly ReportConnectPlatform[] = ["gbp", "facebook", "instagram", "instagram_direct"];

// Finishes an OAuth connect started from the report builder: saves the new token, then returns to the report with a fresh data load
export const useCompleteReportConnect = () => {
  const { platform, reportId = "", businessId = "", accountId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const started = useRef(false);
  const [picker, setPicker] = useState<Picker | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);

  const returnToReport = useCallback(
    (refresh: boolean) => {
      navigate(`${routes.reportBuilder}?view=editor&id=${reportId}`, {
        replace: true,
        state: refresh ? { refreshReportData: true } : undefined,
      });
    },
    [navigate, reportId],
  );

  const fail = (message: string) => {
    dispatch(showSnackbar({ message, type: "error" }));
    returnToReport(false);
  };

  // Inside the picker a failure keeps the dialog open for a retry; otherwise there is nothing left to do here
  const settle = (succeeded: boolean, inPicker: boolean) => {
    if (succeeded) returnToReport(true);
    else if (inPicker) setConnectingId(null);
    else returnToReport(false);
  };

  const connectFacebookPage = async (page: FacebookPage, inPicker: boolean) => {
    setConnectingId(page.page_id);
    const result = await dispatch(
      ConnectFacebookPageAction({
        business_id: businessId,
        page_id: page.page_id,
        page_access_token: page.page_access_token,
      }),
    );
    settle(ConnectFacebookPageAction.fulfilled.match(result), inPicker);
  };

  const connectInstagramAccount = async (
    account: InstagramAccount,
    userAccessToken: string | undefined,
    inPicker: boolean,
  ) => {
    setConnectingId(account.page_id);
    const result = await dispatch(
      connectInstagramPortfolioAction({
        business_id: businessId,
        page_id: account.page_id,
        page_access_token: account.page_access_token,
        page_tasks: account.page_tasks,
        instagram_account_id: account.instagram_business_account?.id,
        user_access_token: userAccessToken,
      }),
    );
    settle(connectInstagramPortfolioAction.fulfilled.match(result), inPicker);
  };

  const completeGbp = async ({ bridgeToken, code, workspaceId }: ICredentials) => {
    const exchange = await dispatch(
      exchangeReconnectGBPTokenAction({ bridge_token: bridgeToken, code, workspaceId }),
    );
    dispatch(clearGBPReconnectPageData());
    if (!exchangeReconnectGBPTokenAction.fulfilled.match(exchange)) {
      fail("Could not verify the Google connection. Please try again.");
      return;
    }
    const data = (exchange.payload as IExchangePayload)?.data;
    const location = data?.locations?.[0];
    if (!location) {
      fail("No Google Business Profile location matched this business. Reconnect with the Google account that manages it.");
      return;
    }
    const saved = await dispatch(
      reconnectGBPPlatformAction({
        business_id: businessId,
        google_location_id: location.google_location_id,
        google_business_account_id: location.google_business_account_id,
        access_token: data?.access_token,
        refresh_token: data?.refresh_token,
        token_expiry: data?.token_expiry,
      }),
    );
    if (!reconnectGBPPlatformAction.fulfilled.match(saved)) {
      fail("Could not save the Google connection. Please try again.");
      return;
    }
    dispatch(showSnackbar({ message: "Google Business Profile reconnected", type: "success" }));
    returnToReport(true);
  };

  const completeFacebook = async ({ bridgeToken, code, workspaceId }: ICredentials) => {
    const exchange = await dispatch(
      facebookExchangeTokenAction({ bridge_token: bridgeToken, code, workspaceId }),
    );
    dispatch(clearFacebookPagesData());
    if (!facebookExchangeTokenAction.fulfilled.match(exchange)) {
      returnToReport(false);
      return;
    }
    const pages = (exchange.payload as IExchangePayload)?.data?.pages ?? [];
    if (pages.length === 0) {
      fail("No Facebook pages were found for this account.");
      return;
    }
    // Reconnecting the page that is already connected needs no choice; anything else is a normal connect with the list
    const current = accountId ? pages.find((page) => page.page_id === accountId) : undefined;
    if (current) {
      await connectFacebookPage(current, false);
      return;
    }
    setPicker({ kind: "facebook", pages });
  };

  const completeInstagram = async ({ bridgeToken, code, workspaceId }: ICredentials) => {
    const exchange = await dispatch(
      instagramPortfolioExchangeTokenAction({ bridge_token: bridgeToken, code, workspaceId }),
    );
    dispatch(clearInstagramPagesData());
    if (!instagramPortfolioExchangeTokenAction.fulfilled.match(exchange)) {
      returnToReport(false);
      return;
    }
    const data = (exchange.payload as IExchangePayload)?.data;
    const accounts = data?.accounts ?? [];
    if (accounts.length === 0) {
      fail("No Instagram business accounts were found for this account.");
      return;
    }
    const current = accountId
      ? accounts.find((account) => account.instagram_business_account?.id === accountId)
      : undefined;
    if (current) {
      await connectInstagramAccount(current, data?.user_access_token, false);
      return;
    }
    setPicker({ kind: "instagram", accounts, userAccessToken: data?.user_access_token });
  };

  // The direct Instagram exchange saves the integration itself, like YouTube and TikTok
  const completeInstagramDirect = async ({ bridgeToken, code, workspaceId }: ICredentials) => {
    const exchange = await dispatch(
      instagramExchangeTokenAction({ bridge_token: bridgeToken, code, workspaceId }),
    );
    dispatch(clearInstagramPagesData());
    returnToReport(instagramExchangeTokenAction.fulfilled.match(exchange));
  };

  // The YouTube and TikTok exchanges save the integration themselves
  const completeYoutube = async ({ bridgeToken, workspaceId }: ICredentials) => {
    const exchange = await dispatch(
      youtubeExchangeTokenAction({ bridge_token: bridgeToken, workspaceId }),
    );
    returnToReport(youtubeExchangeTokenAction.fulfilled.match(exchange));
  };

  const completeTiktok = async ({ bridgeToken, workspaceId }: ICredentials) => {
    const exchange = await dispatch(
      tiktokExchangeTokenAction({ bridge_token: bridgeToken, workspaceId }),
    );
    returnToReport(tiktokExchangeTokenAction.fulfilled.match(exchange));
  };

  useEffect(() => {
    // The authorization code is single use, so the exchange must never run twice
    if (started.current) return;
    started.current = true;

    const bridgeToken = searchParams.get("bridge_token");
    const code = searchParams.get("code") ?? "";
    const workspaceId = localStorage.getItem("activeWorkspaceId");
    if (
      !isReportConnectPlatform(platform) ||
      !reportId ||
      !businessId ||
      !bridgeToken ||
      !workspaceId ||
      (PLATFORMS_WITH_CODE.includes(platform) && !code)
    ) {
      returnToReport(false);
      return;
    }

    const credentials: ICredentials = { bridgeToken, code, workspaceId };
    const completers: Record<ReportConnectPlatform, (c: ICredentials) => Promise<void>> = {
      gbp: completeGbp,
      facebook: completeFacebook,
      instagram: completeInstagram,
      instagram_direct: completeInstagramDirect,
      youtube: completeYoutube,
      tiktok: completeTiktok,
    };
    completers[platform](credentials).catch(() => returnToReport(false));
    // Runs once per landing; the guard above blocks any second run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    picker,
    connectingId,
    connectFacebookPage: (page: FacebookPage) => connectFacebookPage(page, true),
    connectInstagramAccount: (account: InstagramAccount) =>
      connectInstagramAccount(
        account,
        picker?.kind === "instagram" ? picker.userAccessToken : undefined,
        true,
      ),
    cancel: () => returnToReport(false),
  };
};
